/**
 * Mission API Hooks
 *
 * React Query hooks for mission CRUD operations
 * Based on API docs: https://mavlink-be.democoncept.id/swagger-ui/index.html
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api-client';
import { missionQueryKeys } from './mission.keys';

// =============================================================================
// API Types (based on Swagger documentation)
// =============================================================================

/**
 * Mission status enum from API
 */
export type MissionApiStatus = 'DRAFT' | 'READY' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

/**
 * Waypoint action enum from API
 */
export type WaypointAction = 'WAYPOINT' | 'TAKEOFF' | 'LAND' | 'LOITER' | 'RTL';

/**
 * Waypoint response from API
 */
export interface WaypointResponse {
    waypointId: string;
    sequenceNumber: number;
    latitude: number;
    longitude: number;
    altitude?: number;
    speed?: number;
    holdTime?: number;
    action?: string;
}

/**
 * Waypoint request for creating/adding waypoints
 */
export interface WaypointRequest {
    sequenceNumber?: number;
    latitude: number;
    longitude: number;
    altitude?: number;
    speed?: number;
    holdTime?: number;
    action?: WaypointAction;
}

/**
 * Mission response from API
 */
export interface MissionResponse {
    missionId: string;
    missionName: string;
    description?: string;
    status: MissionApiStatus;
    waypoints: WaypointResponse[];
    flightCount: number;
    createdAt: string;
    updatedAt: string;
}

/**
 * Mission create request
 */
export interface MissionCreateRequest {
    missionName: string;
    description?: string;
    waypoints?: WaypointRequest[];
}

/**
 * Mission update request
 */
export interface MissionUpdateRequest {
    missionName?: string;
    description?: string;
    status?: MissionApiStatus;
}

/**
 * Paginated mission response
 */
export interface PageMissionResponse {
    content: MissionResponse[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
}

/**
 * Mission list query params
 */
export interface MissionListParams {
    page?: number;
    size?: number;
    search?: string;
    status?: MissionApiStatus;
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch paginated list of missions
 */
export function useMissionsQuery(params: MissionListParams = {}) {
    const { page = 0, size = 20, search, status } = params;

    return useQuery({
        queryKey: missionQueryKeys.list(params),
        queryFn: async () => {
            const queryParams = new URLSearchParams();
            queryParams.set('page', String(page));
            queryParams.set('size', String(size));
            if (search) queryParams.set('search', search);
            if (status) queryParams.set('status', status);

            return apiGet<PageMissionResponse>(`/api/missions?${queryParams.toString()}`);
        },
        staleTime: 2 * 60 * 1000, // 2 minutes
    });
}

/**
 * Fetch a single mission by ID
 */
export function useMissionQuery(id: string | null) {
    return useQuery({
        queryKey: missionQueryKeys.detail(id || ''),
        queryFn: async () => {
            if (!id) throw new Error('Mission ID is required');
            return apiGet<MissionResponse>(`/api/missions/${id}`);
        },
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Create a new mission
 */
export function useCreateMissionMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: MissionCreateRequest) => {
            return apiPost<MissionResponse>('/api/missions', data);
        },
        onSuccess: () => {
            // Invalidate missions list to refetch
            queryClient.invalidateQueries({
                queryKey: missionQueryKeys.lists(),
            });
        },
        onError: (error) => {
            console.error('[Mission API] Create failed:', error);
        },
    });
}

/**
 * Update a mission
 */
export function useUpdateMissionMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: MissionUpdateRequest }) => {
            return apiPut<MissionResponse>(`/api/missions/${id}`, data);
        },
        onSuccess: (data) => {
            // Invalidate and refetch the specific mission
            queryClient.invalidateQueries({
                queryKey: missionQueryKeys.detail(data.missionId),
            });
            // Also invalidate all missions list
            queryClient.invalidateQueries({
                queryKey: missionQueryKeys.lists(),
            });
        },
        onError: (error) => {
            console.error('[Mission API] Update failed:', error);
        },
    });
}

/**
 * Delete a mission
 */
export function useDeleteMissionMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return apiDelete<void>(`/api/missions/${id}`);
        },
        onSuccess: (_, id) => {
            // Invalidate the deleted mission
            queryClient.invalidateQueries({
                queryKey: missionQueryKeys.detail(id),
            });
            // Invalidate all missions list
            queryClient.invalidateQueries({
                queryKey: missionQueryKeys.lists(),
            });
        },
        onError: (error) => {
            console.error('[Mission API] Delete failed:', error);
        },
    });
}

/**
 * Add a waypoint to a mission
 */
export function useAddWaypointMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ missionId, waypoint }: { missionId: string; waypoint: WaypointRequest }) => {
            return apiPost<MissionResponse>(`/api/missions/${missionId}/waypoints`, waypoint);
        },
        onSuccess: (data) => {
            // Invalidate the mission to refetch with new waypoint
            queryClient.invalidateQueries({
                queryKey: missionQueryKeys.detail(data.missionId),
            });
        },
        onError: (error) => {
            console.error('[Mission API] Add waypoint failed:', error);
        },
    });
}

/**
 * Remove a waypoint from a mission
 */
export function useRemoveWaypointMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ missionId, waypointId }: { missionId: string; waypointId: string }) => {
            return apiDelete<void>(`/api/missions/${missionId}/waypoints/${waypointId}`);
        },
        onSuccess: (_, { missionId }) => {
            // Invalidate the mission to refetch without the waypoint
            queryClient.invalidateQueries({
                queryKey: missionQueryKeys.detail(missionId),
            });
        },
        onError: (error) => {
            console.error('[Mission API] Remove waypoint failed:', error);
        },
    });
}
